/**
 * Workers roster in the left column: every own worker under the task it is on
 * (a farm's or mine's slots, a wild resource, a site, hauling, idle). A row
 * selects its worker (shift adds), shows the status text, the home city chip
 * (lit when it works for another city), a pin when held, and a ▾ menu to
 * hold it on a task (`assign`, pinned) or release it to automation. The
 * grouping lives in `game/view/workerRoster.ts`.
 */
import { type ReactNode, useEffect, useState } from 'react';
import { unitGlyph } from '@/game/rules/ui';
import type { Command } from '@/game/sim';
import { tickRoster } from '@/game/view/tickCache';
import {
  cityTag,
  groupTitle,
  idNumber,
  type RosterWorker,
  slotText,
  type TaskOption,
  taskLabel,
} from '@/game/view/workerRoster';
import { useSimFrame } from './app/simFrame';
import { CollapseToggle, usePersisted } from './persisted';

interface WorkersPanelProps {
  selectedUnitIds: readonly string[];
  /** `additive` (shift held) adds/removes the worker instead of replacing the selection. */
  onSelectUnit: (id: string, additive: boolean) => void;
  /** A group header: select these workers (`additive`: add or remove them). */
  onSelectMany: (ids: string[], additive: boolean) => void;
  dispatch: (command: Command) => void;
  /** The Units | Buildings | Workers tabs. */
  header: ReactNode;
}

export function WorkersPanel({
  selectedUnitIds,
  onSelectUnit,
  onSelectMany,
  dispatch,
  header,
}: WorkersPanelProps) {
  const [collapsed, setCollapsed] = usePersisted('workersCollapsed', false);
  // The worker whose ▾ menu is open.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // The menu closes on a press outside it, Escape, or any selection change.
  useEffect(() => {
    if (menuFor === null) return;
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element) || !e.target.closest('[data-worker-menu]')) {
        setMenuFor(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuFor(null);
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuFor]);
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain, cityNumbers } = frame;
  const roster = tickRoster(state, { terrain, rules, playerId });

  function row(w: RosterWorker, taskKey: string) {
    const u = w.unit;
    const selected = selectedUnitIds.includes(u.id);
    const away = u.homeCityId !== undefined && w.workCityId !== u.homeCityId;
    return (
      <div key={u.id}>
        {/* biome-ignore lint/a11y/useSemanticElements: a <button> nests inside (the menu toggle) */}
        <div
          role="button"
          tabIndex={0}
          aria-label={`Select ${rules.units[u.type]?.name ?? u.type} ${idNumber(u.id)}`}
          onClick={(e) => {
            setMenuFor(null);
            onSelectUnit(u.id, e.shiftKey);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectUnit(u.id, e.shiftKey);
            }
          }}
          className={`flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-left transition ${selected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
        >
          <span className="flex-shrink-0 font-medium">
            {unitGlyph(rules, u.type)} <span className="tabular-nums">{idNumber(u.id)}</span>
          </span>
          <span className="flex-1 truncate text-[11px] text-fg-subtle">{w.status}</span>
          <span
            title={
              away
                ? `Works for ${cityTag(cityNumbers, w.workCityId)}, lives in ${cityTag(cityNumbers, u.homeCityId)}`
                : 'Home city'
            }
            className={`chip px-1.5 text-[10px] ${away ? 'text-accent ring-1 ring-accent/60' : 'text-fg-muted'}`}
          >
            {cityTag(cityNumbers, u.homeCityId)}
          </span>
          <span
            title={w.pinned ? 'Pinned: holds this task until released' : undefined}
            className={`w-3 text-center text-[10px] ${w.pinned ? '' : 'invisible'}`}
          >
            📌
          </span>
          <button
            type="button"
            aria-label="Reassign"
            data-worker-menu
            aria-expanded={menuFor === u.id}
            title="Hold this worker on a task, or release it to automation"
            onClick={(e) => {
              e.stopPropagation();
              setMenuFor(menuFor === u.id ? null : u.id);
            }}
            className="btn btn-ghost btn-icon h-6 w-5 shrink-0 text-xs"
          >
            ▾
          </button>
        </div>
        {menuFor === u.id && (
          <div data-worker-menu className="mb-1 ml-3 mt-0.5 space-y-0.5 rounded-lg bg-fill p-1">
            {roster.options.map((o) => option(u.id, o, o.key === taskKey))}
            <button
              type="button"
              onClick={() => {
                dispatch({ type: 'automate', unitIds: [u.id] });
                setMenuFor(null);
              }}
              className="btn btn-ghost h-6 w-full justify-between px-2 text-[11px] font-normal"
            >
              <span>Auto (release)</span>
              <span className="text-fg-subtle">📌 off</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  function option(unitId: string, o: TaskOption, current: boolean) {
    const label = taskLabel(o, playerId, cityNumbers, state);
    return (
      <button
        key={o.key}
        type="button"
        disabled={o.full && !current}
        title={o.full && !current ? `${label} is full (${slotText(o.slots)})` : `Hold on ${label}`}
        onClick={() => {
          if (!o.buildingId) return;
          dispatch({ type: 'assign', unitIds: [unitId], target: o.buildingId, pin: true });
          setMenuFor(null);
        }}
        className="btn btn-ghost h-6 w-full justify-between gap-2 px-2 text-[11px] font-normal disabled:opacity-40"
      >
        <span className="truncate">
          {o.kind === 'site' ? 'Build · ' : ''}
          {label}
        </span>
        <span className={`tabular-nums ${o.full ? 'text-warn' : 'text-fg-subtle'}`}>
          {current ? '✓ ' : o.full ? 'full ' : ''}
          {slotText(o.slots)}
        </span>
      </button>
    );
  }

  return (
    <div className="panel pointer-events-auto flex max-h-full min-h-0 w-full flex-col p-3 text-xs">
      <div className="flex shrink-0 items-center justify-between gap-2">
        {header}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          label="workers"
        />
      </div>
      {!collapsed && (
        <div className="min-h-0 overflow-y-auto">
          {roster.groups.length === 0 ? (
            <p className="px-1 pt-1 text-fg-muted">No workers</p>
          ) : (
            roster.groups.map((g) => (
              <div key={g.key} className="mt-2">
                <div className="sticky-heading pb-1">
                  <button
                    type="button"
                    title="Select all of them; shift-click adds them to the selection"
                    onClick={(e) => {
                      setMenuFor(null);
                      onSelectMany(
                        g.workers.map((w) => w.unit.id),
                        e.shiftKey
                      );
                    }}
                    className="flex w-full min-w-0 items-center gap-1 rounded-md px-1 py-0.5 text-left text-fg-muted transition hover:bg-fill hover:text-fg"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {groupTitle(g, playerId, cityNumbers, state)}
                    </span>
                    {/* A building's slots used/total, else the group's size. */}
                    <span className="tabular-nums text-fg-subtle">
                      {g.slots ? slotText(g.slots) : g.workers.length}
                    </span>
                  </button>
                </div>
                <div className="space-y-0.5">{g.workers.map((w) => row(w, g.key))}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
